import { FC, type MouseEvent, useState } from "react";

import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { PageCategory } from "components/server-types";
import { t } from "i18n/i18n";
import { CancelSaveWidget } from "mds/components/CancelSaveWidget";
import { Checkbox } from "mds/components/Checkbox";
import { ListItem } from "mds/components/ListItem";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useAppSelector } from "store";
import {
  selectCurrentCourseId,
  selectCurrentSectionId,
  selectCurrentTermId,
  selectSectionsByTermAndCourse,
} from "store/selectors";

export interface CopyToSectionSubMenuProps {
  category: PageCategory;
  onCancel: () => void;
  onConfirm: (sectionIds: string[]) => void;
}

export const CopyToSectionSubMenu: FC<CopyToSectionSubMenuProps> = ({
  category,
  onCancel,
  onConfirm,
}) => {
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const currentCourseId = useAppSelector(selectCurrentCourseId);
  const currentTermId = useAppSelector(selectCurrentTermId);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(currentCourseId);
  const [selectedTermId, setSelectedTermId] = useState<string | null>(currentTermId);
  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>([]);

  const sections = useAppSelector((s) =>
    selectSectionsByTermAndCourse(s, selectedTermId, selectedCourseId),
  );

  const eligibleSections = sections.filter((section) => section.id !== currentSectionId);
  const allSelected = eligibleSections.length === selectedSectionIds.length;

  const handleCourseChange = (newCourseId: string) => {
    setSelectedCourseId(newCourseId);
    setSelectedSectionIds([]);
  };

  const handleTermChange = (newTermId: string) => {
    setSelectedTermId(newTermId);
    setSelectedSectionIds([]);
  };

  const handleSectionSelected = (event: MouseEvent, sectionId: string) => {
    event.preventDefault();
    event.stopPropagation();

    toggleSectionChecked(sectionId);
  };

  const toggleSectionChecked = (sectionId: string) => {
    const newSelectedSectionIds = [...selectedSectionIds];

    const sectionIndex = newSelectedSectionIds.indexOf(sectionId);
    if (sectionIndex > -1) {
      newSelectedSectionIds.splice(sectionIndex, 1);
    } else {
      newSelectedSectionIds.push(sectionId);
    }

    setSelectedSectionIds(newSelectedSectionIds);
  };

  const onSelectAll = () => {
    setSelectedSectionIds(allSelected ? [] : eligibleSections.map((section) => section.id));
  };

  // NOTE: all buttons require isSelected=false to avoid the selected styling from being applied while the menu is open.
  return (
    <div className="w-full min-w-[400px] bg-black-tint-99 p-3">
      <div className="body-s flex gap-1 pb-5">
        {t(`copy_to_section_sub_menu.title.${category}`)}
      </div>

      <div className="flex items-center justify-between gap-2 pb-2">
        <div className="flex items-center gap-2">
          <CourseMenu selectedCourseId={selectedCourseId} onCourseChange={handleCourseChange} />
          <TermMenu selectedTermId={selectedTermId} onTermChange={handleTermChange} />
        </div>

        <SquareButton isSelected={false} kind="ghost" size="s" onClick={onSelectAll}>
          {t(allSelected ? "common.unselect_all" : "common.select_all")}
        </SquareButton>
      </div>

      <div className="mb-2 max-h-[150px] overflow-y-scroll rounded-lg border border-black-tint-90 p-2">
        {/* TODO: add empty state if the selected and term and course code has no sections */}
        {eligibleSections.length === 0 ? (
          <div className="text-black-tint-70">{t("copy_to_section_sub_menu.no_sections")}</div>
        ) : (
          eligibleSections.map((section) => (
            <div key={section.id}>
              <ListItem
                className="gap-2"
                label={section.title}
                onClick={() => toggleSectionChecked(section.id)}
              >
                <Checkbox
                  checked={selectedSectionIds.includes(section.id)}
                  onContainerClick={(e) => handleSectionSelected(e, section.id)}
                >
                  <span>{section.title}</span>
                </Checkbox>
              </ListItem>
            </div>
          ))
        )}
      </div>

      <div className="right-align pb-2 text-black-tint-70">
        {t("copy_to_section_sub_menu.selected_sections", { count: selectedSectionIds.length })}
      </div>

      <CancelSaveWidget
        className="right"
        isSaveDisabled={selectedSectionIds.length === 0}
        saveTextKey="copy"
        onClickCancel={onCancel}
        onClickSave={() => {
          onConfirm(selectedSectionIds);
          setSelectedSectionIds([]);
        }}
      />
    </div>
  );
};
